import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ALL_STATUSES, STATUS_HEX, isClosed, ageFlag, daysSince, fmtDate, fmtDateTime } from "@/lib/status";
import PageHeader from "@/components/PageHeader";
import StatusBadge, { PriorityBadge, TypeBadge, AgeBadge } from "@/components/StatusBadge";
import AdvanceButton from "@/components/AdvanceButton";
import MoveToMaintenanceButton from "@/components/MoveToMaintenanceButton";
import HoldReasonDialog from "@/components/HoldReasonDialog";
import QrLabel from "@/components/QrLabel";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, MessageSquare, ArrowLeft, Trash2, History } from "lucide-react";
import { toast } from "sonner";

const MANUAL_STATUS_OPTIONS = ALL_STATUSES.filter((status) => !isClosed(status));

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-widest text-[#6b6a68] font-mono">{label}</div>
      <div className="mt-1.5 text-sm text-[#232826]">{children}</div>
    </div>
  );
}

export default function ReworkDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [rework, setRework] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [posting, setPosting] = useState(false);
  const [assignInput, setAssignInput] = useState("");
  const [holdOpen, setHoldOpen] = useState(false);

  const role = user?.role;
  const canEdit = role === "admin" || role === "lead";
  const canAdvance = canEdit || role === "kiosk";
  const canComment = !["management", "kiosk"].includes(role);
  const scanMode = params.get("scan") === "1";

  const load = async () => {
    try {
      const [r, c] = await Promise.all([api.get(`/reworks/${id}`), api.get(`/reworks/${id}/comments`)]);
      setRework(r.data);
      setComments(c.data);
      setAssignInput(r.data.assigned_to || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  const updateField = async (patch) => {
    try {
      const { data } = await api.patch(`/reworks/${id}`, patch);
      setRework(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const changeStatus = (v) => {
    if (v === rework.status) return;
    if (v === "On Hold") { setHoldOpen(true); return; }
    updateField({ status: v });
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/reworks/${id}/comments`, { message: msg.trim() });
      setComments((cs) => [...cs, data]);
      setMsg("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Post failed");
    } finally { setPosting(false); }
  };

  const doDelete = async () => {
    if (!window.confirm("Delete this rework request? This cannot be undone.")) return;
    try { await api.delete(`/reworks/${id}`); toast.success("Deleted"); nav("/register"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  if (loading || !rework) return <div className="p-10 font-mono text-sm text-[#6b6a68]" data-testid="detail-loading">Loading…</div>;

  const daysOpen = daysSince(rework.date_submitted);
  const flag = ageFlag(rework);

  return (
    <div>
      <PageHeader title={`${rework.job_number} · ${rework.part_number}`} subtitle={rework.part_name || rework.description} testid="detail-header"
        actions={
          <>
            <Link to="/register"><Button variant="ghost" className="text-[#6b6a68] hover:text-[#424e43] hover:bg-[#efece5] rounded-sm" data-testid="detail-back"><ArrowLeft size={14} className="mr-1.5" /> Register</Button></Link>
            {role === "admin" && <Button variant="ghost" onClick={doDelete} className="text-[#8a3a2a] hover:text-[#6a2a1e] hover:bg-[#8a3a2a]/10 rounded-sm" data-testid="detail-delete"><Trash2 size={14} className="mr-1.5" /> Delete</Button>}
          </>
        } />

      <div className="p-6 md:p-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {(scanMode || role === "kiosk") && canAdvance && !isClosed(rework.status) && (
            <div className="border-2 border-[#424e43] bg-white rounded-sm p-5" data-testid="scan-advance-panel">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6a68] font-mono mb-3">Scanned — update stage</div>
              <AdvanceButton rework={rework} size="lg" testid="detail-advance-lg" onDone={setRework} />
            </div>
          )}

          <div className="border border-[#e6e5df] bg-white rounded-sm p-6">
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <StatusBadge status={rework.status} testid="detail-status-badge" />
              <TypeBadge type={rework.request_type} testid="detail-type-badge" />
              <PriorityBadge priority={rework.priority} testid="detail-priority-badge" />
              <AgeBadge flag={flag} testid="detail-age-badge" />
              {canAdvance && !isClosed(rework.status) && !scanMode && role !== "kiosk" && <div className="ml-auto"><AdvanceButton rework={rework} testid="detail-advance" onDone={setRework} /></div>}
              {role !== "kiosk" && <div className="ml-auto"><MoveToMaintenanceButton rework={rework} onDone={setRework} /></div>}
              {rework.status === "In Maintenance Clearing" && role !== "kiosk" && (
                <Link to="/maintenance" className="ml-auto text-xs uppercase tracking-widest text-amber-800 underline-offset-4 hover:underline" data-testid="detail-go-maintenance">Record pickup →</Link>
              )}
            </div>
            {rework.status === "On Hold" && rework.hold_reason && (
              <div className="mb-6 text-sm text-orange-900 bg-orange-50 border border-orange-200 rounded-sm px-3 py-2" data-testid="detail-hold-reason"><span className="font-semibold">On hold:</span> {rework.hold_reason}</div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {rework.part_name && <Field label="Part name"><span data-testid="detail-part-name" className="font-semibold text-[#232826]">{rework.part_name}</span></Field>}
              <Field label="Reason">{rework.rework_reason}</Field>
              <Field label="Days open"><span className={`num text-2xl ${flag?.level === "overdue" ? "text-[#8a3a2a]" : flag ? "text-[#a06b3a]" : "text-[#424e43]"}`} data-testid="days-open">{daysOpen}d</span></Field>
              <Field label="Submitted">{fmtDate(rework.date_submitted)}</Field>
              <Field label="Required by">{rework.required_by ? fmtDate(rework.required_by) : <span className="text-[#a39f98] italic">Not set</span>}</Field>
              {rework.bay_number !== null && rework.bay_number !== undefined && (
                <Field label="Completion bay">
                  <span data-testid="detail-completion-bay">
                    Bay {rework.bay_number} · {rework.completion_destination}
                  </span>
                </Field>
              )}
            </div>
            <div className="border-t border-[#e6e5df] mt-6 pt-6 grid grid-cols-2 gap-6">
              <Field label="Requested by">{rework.requested_by_name}</Field>
              <Field label="Assigned to">{rework.assigned_to || <span className="text-[#a39f98] italic">Unassigned</span>}</Field>
              <Field label="Stock checked">{rework.stock_checked ? "Yes — material available" : <span className="text-[#a39f98] italic">Not yet</span>}</Field>
              {rework.date_completed && <Field label="Completed">{fmtDate(rework.date_completed)}</Field>}
              {rework.pickup && (
                <Field label="Left factory" className="col-span-2">
                  <div data-testid="detail-pickup">
                    <span className="font-medium">{rework.pickup.by}</span> · {fmtDateTime(rework.pickup.at)}
                    {rework.pickup.note && <div className="text-xs text-[#6b6a68] mt-0.5">{rework.pickup.note}</div>}
                    <div className="text-[10px] uppercase tracking-widest text-[#a39f98] mt-1">Recorded by {rework.pickup.recorded_by}</div>
                  </div>
                </Field>
              )}
              <Field label="Description" className="col-span-2"><p className="leading-relaxed">{rework.description}</p></Field>
              {rework.comments && <Field label="Submitter notes" className="col-span-2"><p className="text-[#424e43]">{rework.comments}</p></Field>}
              {rework.search_checklist?.length > 0 && (
                <Field label="Search performed before logging" className="col-span-2">
                  <ul className="text-xs text-[#424e43] list-disc pl-4 space-y-0.5">{rework.search_checklist.map((c) => <li key={c}>{c}</li>)}</ul>
                </Field>
              )}
            </div>
          </div>

          <div className="border border-[#e6e5df] bg-white rounded-sm p-6" data-testid="history-section">
            <div className="flex items-center gap-2 mb-4"><History size={16} className="text-[#647265]" /><div className="font-display font-bold text-lg text-[#232826]">Stage history</div></div>
            <ol className="relative border-l border-[#e6e5df] ml-2 space-y-4">
              {rework.status_history.map((h, i) => (
                <li key={`${h.status}-${h.at}`} className="ml-5" data-testid={`history-${i}`}>
                  <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full border-2 border-white" style={{ backgroundColor: STATUS_HEX[h.status] || "#94a3b8" }} />
                  <div className="text-sm font-medium text-[#232826]">{h.status}</div>
                  <div className="text-[11px] font-mono text-[#a39f98]">{fmtDateTime(h.at)}{h.by ? ` · ${h.by}` : ""}</div>
                </li>
              ))}
            </ol>
          </div>

          <div className="border border-[#e6e5df] bg-white rounded-sm p-6" data-testid="activity-section">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare size={16} className="text-[#647265]" />
              <div className="font-display font-bold text-lg text-[#232826]">Activity</div>
              <div className="ml-auto text-xs text-[#6b6a68] font-mono">{comments.length} comment{comments.length !== 1 ? "s" : ""}</div>
            </div>
            <div className="space-y-4">
              {comments.length === 0 && <div className="text-sm text-[#a39f98] py-4 text-center">No comments yet.</div>}
              {comments.map((c) => (
                <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                  <div className="w-8 h-8 rounded-sm bg-[#efece5] flex items-center justify-center text-xs font-medium text-[#424e43] shrink-0">{(c.user_name || "?").slice(0, 1).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2"><div className="text-sm font-medium text-[#232826]">{c.user_name}</div><div className="text-[11px] text-[#a39f98] font-mono">{fmtDateTime(c.created_at)}</div></div>
                    <p className="mt-1 text-sm text-[#424e43] leading-relaxed">{c.message}</p>
                  </div>
                </div>
              ))}
            </div>
            {canComment ? (
              <form onSubmit={addComment} className="mt-6 pt-6 border-t border-[#e6e5df] space-y-3" data-testid="comment-form">
                <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} placeholder="Add a comment…" data-testid="comment-input" className="bg-white border-[#cfcfc9] rounded-sm resize-none text-[#232826]" />
                <div className="flex justify-end">
                  <Button type="submit" disabled={posting || !msg.trim()} data-testid="comment-submit" className="bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] rounded-sm uppercase text-xs tracking-widest">{posting ? "Posting…" : "Post comment"}</Button>
                </div>
              </form>
            ) : (
              <div className="mt-6 pt-6 border-t border-[#e6e5df] flex items-center gap-2 text-xs text-[#a39f98]"><Lock size={12} /> This account is read-only for comments.</div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="border border-[#e6e5df] bg-white rounded-sm p-6" data-testid="admin-panel">
            <div className="font-display font-bold text-lg mb-4 text-[#232826]">Controls</div>
            {canEdit ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Status</Label>
                  {!isClosed(rework.status) ? (
                    <Select value={rework.status} onValueChange={changeStatus}>
                      <SelectTrigger
                        className="mt-2 rounded-sm border-[#cfcfc9] bg-white text-[#232826]"
                        data-testid="detail-status-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-[#e6e5df] bg-white">
                        {MANUAL_STATUS_OPTIONS.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <StatusBadge status={rework.status} testid="detail-completed-status" />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Priority</Label>
                    <Select value={rework.priority} onValueChange={(v) => updateField({ priority: v })}>
                      <SelectTrigger className="bg-white border-[#cfcfc9] rounded-sm mt-2 text-[#232826]" data-testid="detail-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-[#e6e5df]">{["Low", "Normal", "Urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Type</Label>
                    <Select value={rework.request_type} onValueChange={(v) => updateField({ request_type: v })}>
                      <SelectTrigger className="bg-white border-[#cfcfc9] rounded-sm mt-2 text-[#232826]" data-testid="detail-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-[#e6e5df]">{["Rework", "Missing"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Required by</Label>
                  <Input type="date" value={rework.required_by || ""} onChange={(e) => updateField({ required_by: e.target.value || "" })} data-testid="detail-required-by" className="bg-white border-[#cfcfc9] rounded-sm mt-2 text-[#232826]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Assigned to</Label>
                  <div className="flex gap-2 mt-2">
                    <Input value={assignInput} onChange={(e) => setAssignInput(e.target.value)} placeholder="Team member name" data-testid="detail-assign-input" className="bg-white border-[#cfcfc9] rounded-sm text-[#232826]" />
                    <Button type="button" onClick={() => updateField({ assigned_to: assignInput })} data-testid="detail-assign-save" className="bg-[#e6e5df] hover:bg-[#cfcfc9] border border-[#cfcfc9] rounded-sm text-[#232826]">Save</Button>
                  </div>
                </div>
                <label className="flex items-center gap-3 text-sm text-[#232826] cursor-pointer pt-1">
                  <Checkbox checked={rework.stock_checked} onCheckedChange={(v) => updateField({ stock_checked: !!v })} data-testid="detail-stock-checked" />
                  Stock / material checked and available
                </label>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-[#a39f98] py-2"><Lock size={14} /> Only Admins and Leads can update this record.</div>
            )}
          </div>
          <QrLabel rework={rework} />
        </div>
      </div>
      <HoldReasonDialog open={holdOpen} onOpenChange={setHoldOpen} onConfirm={(reason) => { updateField({ status: "On Hold", hold_reason: reason }); setHoldOpen(false); }} />
    </div>
  );
}
